import { useId } from 'react';
import type { CompareCard } from '../../../data/referenceGuides/types';
interface CompareBlockProps {
  cards?: CompareCard[];
  columns: string[];
  rows: string[][];
  caption: string;
}

/**
 * Head-to-head comparison: optional intro cards plus an attribute table.
 *
 * Design review trimmed this: cards only appear where the two sides need prose
 * the table cannot carry (PR vs PO, ERP vs MES). Where the cards would just
 * restate the table, the data omits them and the table stands alone.
 *
 * Both parts are optional, so one component covers "cards only"
 * (RFQs & approvals) and "table only" (ERP inventory vs standalone).
 */
export function CompareBlock({
  cards,
  columns,
  rows,
  caption
}: CompareBlockProps) {
  const captionId = useId();
  const hasTable = columns.length > 0 && rows.length > 0;
  return <div className="space-y-6" data-id="element-250" data-mp="gsdtmmz6ip6q">
      {cards && cards.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-251" data-mp="lrgyo7f5svep">
          {cards.map(card => {
        const accent = card.emphasis === 'warn' ? {
          badge: 'bg-magenta-hot/15 text-magenta-hot',
          dot: 'bg-magenta-hot'
        } : card.emphasis === 'accent' ? {
          badge: 'bg-guide-procurement/15 text-guide-procurement',
          dot: 'bg-guide-procurement'
        } : {
          badge: 'bg-violet-link/15 text-violet-link',
          dot: 'bg-violet-link'
        };
        return <div key={card.tag} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6" data-id="element-252" data-mp="394fv3b2665x0">
                <span className={`inline-flex w-11 h-10 rounded-lg ${accent.badge} items-center justify-center text-xs font-bold mb-4`} data-id="element-253" data-mp="34cxref8rhr8c">
                  {card.tag}
                </span>
                <p className="text-white font-semibold text-base mb-2" data-id="element-254" data-mp="2vd1a4yopbunb">{card.title}</p>
                <p className="text-white/55 text-sm leading-relaxed mb-5" data-id="element-255" data-mp="2qd564zg0837r">{card.body}</p>
                <ul className="space-y-2" data-id="element-256" data-mp="2lebr32l2k45h">
                  {card.bullets.map(b => <li key={b} className="flex gap-2.5 text-white/60 text-sm leading-snug" data-id="element-257" data-mp="2lett6fuexclb">
                      <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${accent.dot} mt-1.5 flex-shrink-0`} data-id="element-258" data-mp="2bp4tw8abx7y4" />
                      {b}
                    </li>)}
                </ul>
              </div>;
      })}
        </div>}

      {hasTable && <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden" data-id="element-259" data-mp="26rd1ld5rvjte">
          <div role="region" aria-labelledby={captionId} tabIndex={0} className="overflow-x-auto focus-visible:outline-2 focus-visible:outline-violet-link" data-id="element-260" data-mp="2uh7gs1dkdkgt">
            <table className="w-full text-left border-collapse min-w-[560px]" data-id="element-261" data-mp="2zfqxnndaw62b">
              <caption id={captionId} className="sr-only" data-id="element-262" data-mp="2kqcnid1whklv">
                {caption}
              </caption>
              <thead data-id="element-263" data-mp="2pp62k9wu5jo5">
                <tr className="border-b border-white/[0.1] bg-white/[0.03]" data-id="element-264" data-mp="2poe8x4mstw0i">
                  {columns.map((col, i) => <th key={col} scope="col" className={`py-3.5 px-5 text-xs font-semibold uppercase tracking-widest ${i === 0 ? 'text-white/60 w-1/4' : 'text-violet-link'}`} data-id="element-265" data-mp="3ddyttt2ij3cf" data-mpi={i}>
                      {col}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-266" data-mp="11rwuta54u3dy">
                {rows.map((row, i) => <tr key={row[0]} className={i < rows.length - 1 ? 'border-b border-white/[0.06]' : undefined} data-id="element-267" data-mp="1ghmccfxj8k0e" data-mpi={i}>
                    <th scope="row" className="py-3 px-5 text-white/70 text-sm font-medium text-left" data-id="element-268" data-mp="1bhzolck2vldc">
                      {row[0]}
                    </th>
                    {row.slice(1).map((cell, j) => <td key={columns[j + 1]} className="py-3 px-5 text-white/55 text-sm" data-id="element-269" data-mp="iu3u8ehze5jq" data-mpi={j}>
                        {cell}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}